import { axiosConfig } from "@/services/axiosConfig";
import type { Submission, SubmissionStatus } from "@/projects/lib/store";
import {
  buildPageParams,
  extractPaginationMeta,
  type PaginatedResult,
} from "@/projects/lib/pagination";

const SUBMISSIONS_PATH = "/rajnagar_gyanmahostav/student-video-submissions/";

/** Raw API record — fields may vary; mapper is defensive. */
export type ApiStudentVideoSubmission = {
  id?: string | number;
  reference?: string;
  reference_number?: string;
  student_name?: string;
  studentName?: string;
  student_id?: string;
  studentId?: string;
  pathshala_name?: string;
  phone_number?: string;
  birth_date?: string;
  age?: number | string;
  remark?: string;
  topic?: string | number | { id?: string | number; name?: string } | null;
  topic_id?: string | number | null;
  topic_name?: string | null;
  video_title?: string;
  videoTitle?: string;
  video_attachment?: string | null;
  video_url?: string | null;
  file_name?: string;
  file_size?: number;
  file_size_mb?: number;
  file_size_bytes?: number;
  status?: string;
  submitted_at?: string;
  created_at?: string;
  created?: string;
  assigned_teacher?: string | number | { id?: string | number; name?: string } | null;
  assigned_teacher_id?: string | number | null;
  assigned_teacher_name?: string | null;
  assigned_at?: string | null;
  teacher_remarks?: string | null;
  verified_at?: string | null;
  reward_number?: number | string | null;
  reward?: number | string | null;
  assignment_notes?: string | null;
  timeline?: { at?: string; label?: string; created_at?: string; event?: string }[];
  [key: string]: unknown;
};

type ListPayload =
  | ApiStudentVideoSubmission[]
  | {
      success?: boolean;
      message?: string;
      results?: ApiStudentVideoSubmission[];
      data?: ApiStudentVideoSubmission[] | { results?: ApiStudentVideoSubmission[] };
      pagination?: {
        count?: number;
        current_page?: number;
        total_pages?: number;
        next?: string | null;
        previous?: string | null;
      };
      count?: number;
    };

/** Verification history list item from /verification-history/ */
export type ApiVerificationHistoryItem = {
  id?: string | number;
  reference?: string;
  student?: string;
  title?: string;
  status?: string;
  status_display?: string;
  verified?: string;
  remarks?: string | null;
  [key: string]: unknown;
};

export type VerificationHistoryRow = {
  id: string;
  reference: string;
  student: string;
  title: string;
  status: SubmissionStatus;
  statusDisplay: string;
  verified: string;
  remarks: string;
};

type HistoryListPayload =
  | ApiVerificationHistoryItem[]
  | {
      success?: boolean;
      message?: string;
      data?: ApiVerificationHistoryItem[];
      results?: ApiVerificationHistoryItem[];
      pagination?: unknown;
    };

function unwrapHistoryList(
  payload: HistoryListPayload
): ApiVerificationHistoryItem[] {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload.data)) {
    return payload.data;
  }
  if (Array.isArray(payload.results)) {
    return payload.results;
  }
  return [];
}

export function mapVerificationHistoryItem(
  row: ApiVerificationHistoryItem
): VerificationHistoryRow {
  const status = normalizeStatus(row.status_display || row.status);
  return {
    id: row.id !== null && row.id !== undefined ? String(row.id) : row.reference || "—",
    reference: row.reference || (row.id !== null && row.id !== undefined ? String(row.id) : "—"),
    student: row.student?.trim() || "—",
    title: row.title?.trim() || "—",
    status,
    statusDisplay: row.status_display?.trim() || status,
    verified: row.verified || "",
    remarks: (row.remarks ?? "").trim(),
  };
}

function unwrapList(payload: ListPayload): ApiStudentVideoSubmission[] {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload.results)) {
    return payload.results;
  }
  if (payload.data) {
    if (Array.isArray(payload.data)) {
      return payload.data;
    }
    if (Array.isArray(payload.data.results)) {
      return payload.data.results;
    }
  }
  return [];
}

function fileNameFromUrl(url?: string | null): string {
  if (!url) {
    return "";
  }
  try {
    const path = new URL(url, "https://placeholder.local").pathname;
    const name = decodeURIComponent(path.split("/").filter(Boolean).pop() ?? "");
    return name || "video.mp4";
  } catch {
    return url.split("/").pop() || "video.mp4";
  }
}

function sizeMbFromApi(row: ApiStudentVideoSubmission): number {
  if (typeof row.file_size_mb === "number") {
    return Math.round(row.file_size_mb * 10) / 10;
  }
  const bytes = row.file_size_bytes ?? row.file_size;
  if (typeof bytes === "number" && bytes > 0) {
    return Math.round((bytes / (1024 * 1024)) * 10) / 10;
  }
  return 0;
}

function normalizeStatus(raw?: string): SubmissionStatus {
  if (!raw) {
    return "Pending";
  }
  const v = raw.trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (
    ["pending", "submitted", "new", "created"].includes(v)
  ) {
    return "Pending";
  }
  if (
    [
      "assigned",
      "assigned_for_verification",
      "under_verification",
      "in_review",
      "assigned_to_teacher",
    ].includes(v)
  ) {
    return "Assigned for Verification";
  }
  if (["not_proper", "rejected", "invalid"].includes(v)) {
    return "Not Proper";
  }
  if (["not_eligible", "not eligible", "ineligible", "ineligible_submission"].includes(v)) {
    return "Not Eligible";
  }
  if (
    ["ready_for_reward", "approved", "verified", "reward_ready"].includes(v)
  ) {
    return "Ready for Reward";
  }

  // Exact UI labels from backend
  const labeled = raw as SubmissionStatus;
  if (
    labeled === "Pending" ||
    labeled === "Assigned for Verification" ||
    labeled === "Not Proper" ||
    labeled === "Not Eligible" ||
    labeled === "Ready for Reward"
  ) {
    return labeled;
  }
  return "Pending";
}

function teacherFromApi(row: ApiStudentVideoSubmission): {
  id?: string;
  name?: string;
} {
  const nested = row.assigned_teacher;
  if (nested && typeof nested === "object") {
    return {
      id: nested.id !== null && nested.id !== undefined ? String(nested.id) : undefined,
      name: nested.name,
    };
  }
  if (typeof nested === "string" || typeof nested === "number") {
    return {
      id: String(nested),
      name: row.assigned_teacher_name ?? undefined,
    };
  }
  return {
    id:
      row.assigned_teacher_id !== null && row.assigned_teacher_id !== undefined
        ? String(row.assigned_teacher_id)
        : undefined,
    name: row.assigned_teacher_name ?? undefined,
  };
}

export function mapApiSubmissionToSubmission(
  row: ApiStudentVideoSubmission
): Submission {
  const teacher = teacherFromApi(row);
  const videoUrl =
    (typeof row.video_attachment === "string" && row.video_attachment) ||
    (typeof row.video_url === "string" && row.video_url) ||
    undefined;
  const submittedAt =
    row.submitted_at || row.created_at || row.created || new Date().toISOString();
  const verifiedAt =
    row.verified_at ||
    (typeof row.verified_date === "string" ? row.verified_date : undefined) ||
    (typeof row.updated_at === "string" ? row.updated_at : undefined);
  const reference =
    row.reference ||
    row.reference_number ||
    (row.id !== null && row.id !== undefined ? String(row.id) : "—");
  const apiId = row.id !== null && row.id !== undefined ? String(row.id) : undefined;

  const rewardRaw = row.reward_number ?? row.reward;
  const rewardParsed =
    rewardRaw === null || rewardRaw === undefined || rewardRaw === ""
      ? undefined
      : Number(rewardRaw);
  const rewardNumber =
    rewardParsed !== undefined && Number.isFinite(rewardParsed)
      ? rewardParsed
      : undefined;

  const timeline =
    row.timeline?.map(t => ({
      at: t.at || t.created_at || submittedAt,
      label: t.label || t.event || "Update",
    })) ?? [{ at: submittedAt, label: "Submission created" }];

  const topicRaw = row.topic;
  let topicId: string | undefined;
  let topicName: string | undefined;
  if (topicRaw && typeof topicRaw === "object") {
    topicId =
      topicRaw.id !== null && topicRaw.id !== undefined
        ? String(topicRaw.id)
        : undefined;
    topicName = topicRaw.name ?? undefined;
  } else if (typeof topicRaw === "string" || typeof topicRaw === "number") {
    topicId = String(topicRaw);
  }
  if (!topicId && row.topic_id !== null && row.topic_id !== undefined) {
    topicId = String(row.topic_id);
  }
  if (!topicName && row.topic_name) {
    topicName = row.topic_name;
  }

  const ageParsed =
    row.age === null || row.age === undefined || row.age === ""
      ? undefined
      : Number(row.age);

  return {
    id: reference,
    apiId,
    studentName: row.student_name || row.studentName || "—",
    studentId: row.student_id || row.studentId || "—",
    pathshalaName: row.pathshala_name ?? undefined,
    phoneNumber: row.phone_number ?? undefined,
    birthDate: row.birth_date ?? undefined,
    age: ageParsed !== undefined && Number.isFinite(ageParsed) ? ageParsed : undefined,
    remark: row.remark ?? undefined,
    topicId,
    topicName,
    videoTitle: row.video_title || row.videoTitle || "—",
    fileName: row.file_name || fileNameFromUrl(videoUrl) || "video.mp4",
    fileSizeMB: sizeMbFromApi(row),
    submittedAt,
    status: normalizeStatus(row.status),
    assignedTeacherId: teacher.id,
    assignedTeacherName: teacher.name,
    assignedAt: row.assigned_at ?? undefined,
    assignmentNotes: row.assignment_notes ?? undefined,
    teacherRemarks: row.teacher_remarks ?? undefined,
    verifiedAt: verifiedAt ?? undefined,
    rewardNumber,
    videoUrl,
    timeline,
  };
}

/** UI → API status query value (e.g. status=pending). */
export const STATUS_API_VALUE: Record<SubmissionStatus, string> = {
  Pending: "pending",
  "Assigned for Verification": "assigned",
  "Not Proper": "not_proper",
  "Ready for Reward": "ready_for_reward",
};

/** Convert HTML date (yyyy-mm-dd) → API format dd-mm-yyyy. */
export function toApiDate(isoDate: string): string {
  if (!isoDate) {
    return "";
  }
  const [y, m, d] = isoDate.split("-");
  if (!y || !m || !d) {
    return isoDate;
  }
  return `${d}-${m}-${y}`;
}

export type StudentVideoSubmissionFilters = {
  from_date?: string;
  to_date?: string;
  status?: string;
  assigned_teacher?: string;
  topic?: string;
  search?: string;
  student_name?: string;
  student_id?: string;
  video_title?: string;
  reference?: string;
  [key: string]: string | undefined;
};

function buildQueryParams(
  filters?: StudentVideoSubmissionFilters
): Record<string, string> {
  if (!filters) {
    return {};
  }
  const params: Record<string, string> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value !== null && value !== undefined && String(value).trim() !== "") {
      params[key] = String(value).trim();
    }
  }
  return params;
}

const EXPORT_PAGE_SIZE = 100;

export async function fetchStudentVideoSubmissionsRaw(
  filters?: StudentVideoSubmissionFilters
): Promise<ApiStudentVideoSubmission[]> {
  const { data } = await axiosConfig.get<ListPayload>(SUBMISSIONS_PATH, {
    params: { ...buildQueryParams(filters), ...buildPageParams(1, EXPORT_PAGE_SIZE) },
    headers: { Accept: "application/json" },
  });
  return unwrapList(data ?? []);
}

export async function fetchStudentVideoSubmissionsRawAll(
  filters?: StudentVideoSubmissionFilters
): Promise<ApiStudentVideoSubmission[]> {
  const allRows: ApiStudentVideoSubmission[] = [];
  let page = 1;
  let totalPages = 1;

  do {
    const { data } = await axiosConfig.get<ListPayload>(SUBMISSIONS_PATH, {
      params: { ...buildQueryParams(filters), ...buildPageParams(page, EXPORT_PAGE_SIZE) },
      headers: { Accept: "application/json" },
    });

    const rows = unwrapList(data ?? []);
    allRows.push(...rows);

    const pagination = extractPaginationMeta(data ?? {}, rows.length, page);
    totalPages = Math.max(1, pagination.totalPages);
    page += 1;
  } while (page <= totalPages);

  return allRows;
}

export async function fetchStudentVideoSubmissions(
  filters?: StudentVideoSubmissionFilters
): Promise<Submission[]> {
  const allRows: ApiStudentVideoSubmission[] = [];
  let page = 1;
  let totalPages = 1;

  do {
    const { data } = await axiosConfig.get<ListPayload>(SUBMISSIONS_PATH, {
      params: { ...buildQueryParams(filters), ...buildPageParams(page, EXPORT_PAGE_SIZE) },
      headers: { Accept: "application/json" },
    });

    const rows = unwrapList(data ?? []);
    allRows.push(...rows);

    const pagination = extractPaginationMeta(data ?? {}, rows.length, page);
    totalPages = Math.max(1, pagination.totalPages);
    page += 1;
  } while (page <= totalPages);

  return allRows.map(mapApiSubmissionToSubmission);
}

export async function fetchStudentVideoSubmissionsPage(
  filters?: StudentVideoSubmissionFilters,
  page?: number,
  pageSize?: number
): Promise<PaginatedResult<Submission>> {
  const { data } = await axiosConfig.get<ListPayload>(SUBMISSIONS_PATH, {
    params: { ...buildQueryParams(filters), ...buildPageParams(page, pageSize) },
    headers: { Accept: "application/json" },
  });
  const items = unwrapList(data ?? []).map(mapApiSubmissionToSubmission);
  return {
    items,
    pagination: extractPaginationMeta(data ?? {}, items.length, page ?? 1),
  };
}

function unwrapOne(
  payload: ApiStudentVideoSubmission | { data?: ApiStudentVideoSubmission }
): ApiStudentVideoSubmission {
  const candidate = (payload as { data?: ApiStudentVideoSubmission }).data;
  if (candidate) {
    return candidate;
  }
  return payload as ApiStudentVideoSubmission;
}

export async function fetchStudentVideoSubmission(
  id: string
): Promise<Submission> {
  const { data } = await axiosConfig.get<
    ApiStudentVideoSubmission | { data: ApiStudentVideoSubmission }
  >(`${SUBMISSIONS_PATH}${encodeURIComponent(id)}/`, {
    headers: { Accept: "application/json" },
  });
  return mapApiSubmissionToSubmission(unwrapOne(data));
}

export async function deleteStudentVideoSubmission(id: string): Promise<void> {
  await axiosConfig.delete(
    `${SUBMISSIONS_PATH}${encodeURIComponent(id)}/`,
    { headers: { Accept: "application/json" } }
  );
}

export async function bulkDeleteStudentVideoSubmissions(
  ids: (string | number)[]
): Promise<void> {
  const numericIds = ids.map(id => {
    const num = Number(id);
    return Number.isFinite(num) ? num : id;
  });
  await axiosConfig.post(
    `${SUBMISSIONS_PATH}bulk-delete/`,
    { ids: numericIds },
    {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    }
  );
}

export async function assignStudentVideoSubmission(
  id: string,
  assignedTeacher: string,
  assignmentNotes?: string
): Promise<void> {
  await axiosConfig.post(
    `${SUBMISSIONS_PATH}${encodeURIComponent(id)}/assign/`,
    {
      assigned_teacher: assignedTeacher,
      assignment_notes: assignmentNotes?.trim() ?? "",
    },
    {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    }
  );
}

/** Set / update the reward number for a submission (PATCH detail endpoint). */
export async function setSubmissionRewardNumber(
  id: string,
  rewardNumber: number
): Promise<Submission> {
  const { data } = await axiosConfig.patch<
    ApiStudentVideoSubmission | { data: ApiStudentVideoSubmission }
  >(
    `${SUBMISSIONS_PATH}${encodeURIComponent(id)}/`,
    { reward_number: rewardNumber },
    {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    }
  );
  return mapApiSubmissionToSubmission(unwrapOne(data));
}

export async function fetchVerificationHistoryPage(params?: {
  page?: number;
  pageSize?: number;
  search?: string;
}): Promise<PaginatedResult<VerificationHistoryRow>> {
  const query: Record<string, string> = {
    ...buildPageParams(params?.page, params?.pageSize),
  };
  if (params?.search) {
    query.search = params.search;
  }
  const { data } = await axiosConfig.get<HistoryListPayload>(
    `${SUBMISSIONS_PATH}verification-history/`,
    { params: query, headers: { Accept: "application/json" } }
  );
  const items = unwrapHistoryList(data ?? []).map(mapVerificationHistoryItem);
  return {
    items,
    pagination: extractPaginationMeta(data ?? {}, items.length, params?.page ?? 1),
  };
}

export async function fetchVerificationHistory(): Promise<
  VerificationHistoryRow[]
> {
  const { items } = await fetchVerificationHistoryPage({ pageSize: 1000 });
  return items;
}

export async function approveStudentVideoSubmission(
  id: string,
  teacherRemarks: string
): Promise<void> {
  await axiosConfig.post(
    `${SUBMISSIONS_PATH}${encodeURIComponent(id)}/approve/`,
    { teacher_remarks: teacherRemarks },
    {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    }
  );
}

export type SubmissionRejectStatus = "not_proper" | "not_eligible";

export async function rejectStudentVideoSubmission(
  id: string,
  teacherRemarks: string,
  status: SubmissionRejectStatus
): Promise<void> {
  await axiosConfig.post(
    `${SUBMISSIONS_PATH}${encodeURIComponent(id)}/reject/`,
    { teacher_remarks: teacherRemarks, status },
    {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    }
  );
}

type VideoSummaryPayload =
  | string
  | {
      success?: boolean;
      message?: string;
      summary?: string;
      video_summary?: string;
      text?: string;
      remarks?: string;
      result?: string;
      data?:
        | string
        | {
            summary?: string;
            video_summary?: string;
            text?: string;
            remarks?: string;
            result?: string;
          };
    };

function extractVideoSummary(payload: VideoSummaryPayload): string {
  if (typeof payload === "string") {
    return payload.trim();
  }
  if (!payload || typeof payload !== "object") {
    return "";
  }
  const fromData =
    typeof payload.data === "string"
      ? payload.data
      : payload.data?.summary ??
        payload.data?.video_summary ??
        payload.data?.text ??
        payload.data?.remarks ??
        payload.data?.result;
  const summary =
    payload.summary ??
    payload.video_summary ??
    payload.text ??
    payload.remarks ??
    payload.result ??
    fromData ??
    "";
  return summary.trim();
}

/** POST a signed video URL and get an AI-generated summary for remarks. */
export async function fetchVideoSummary(videoUrl: string): Promise<string> {
  const { data } = await axiosConfig.post<VideoSummaryPayload>(
    "/rajnagar_gyanmahostav/video-summary/",
    { video_url: videoUrl },
    {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    }
  );
  return extractVideoSummary(data);
}

export function getSubmissionApiErrorMessage(error: unknown, fallback: string) {
  const err = error as {
    response?: { data?: Record<string, unknown> };
    message?: string;
  };
  const data = err?.response?.data;
  if (!data) {
    return err?.message || fallback;
  }
  for (const key of ["message", "detail", "error", "non_field_errors"]) {
    const v = data[key];
    if (typeof v === "string" && v.trim()) {
      return v;
    }
    if (Array.isArray(v) && typeof v[0] === "string") {
      return v[0];
    }
  }
  return fallback;
}
